import * as React from 'react'
import { DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog'
import { Input } from '../../../components/ui/input'
import { cn } from '../../../lib/utils'

/**
 * Compact chrome shared by the config editor dialogs (checks, description,
 * contract). The dialog is a flex column capped near the viewport height; the
 * body takes whatever is left and scrolls only when the content outgrows it,
 * so short edits are fully visible and long ones still fit on screen.
 */
export const EDITOR_DIALOG_CLASS = 'flex max-h-[88vh] flex-col gap-3 p-5'
export const EDITOR_BODY_CLASS = 'flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1'
export const EDITOR_INPUT_CLASS = 'h-7 px-2 text-xs md:text-xs'
export const EDITOR_SELECT_CLASS = 'px-2 pr-7 text-xs data-[size=sm]:h-7'
export const EDITOR_LABEL_CLASS = 'text-11 font-medium text-ink-3'
export const EDITOR_CARD_CLASS = 'rounded-md border border-line bg-bg-sunk/30 p-2.5'

export function EditorDialogHeader({
  title,
  children
}: {
  title: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <DialogHeader className="gap-1 pr-6">
      <DialogTitle className="text-base">{title}</DialogTitle>
      <DialogDescription className="text-xs leading-5">{children}</DialogDescription>
    </DialogHeader>
  )
}

/** A labelled single-line field with the compact input size. */
export function EditorField({
  label,
  ariaLabel,
  value,
  placeholder,
  onChange,
  className
}: {
  label: React.ReactNode
  /** Accessible name when it should differ from the visible label. */
  ariaLabel?: string
  value: string
  placeholder?: string
  onChange: (value: string) => void
  className?: string
}) {
  return (
    <label className={cn('flex flex-col gap-1', className)}>
      <span className={EDITOR_LABEL_CLASS}>{label}</span>
      <Input
        aria-label={ariaLabel}
        className={EDITOR_INPUT_CLASS}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  )
}

export function EditorProblems({
  problems,
  testId
}: {
  problems: readonly string[]
  testId?: string
}) {
  if (!problems.length) return null
  return (
    <ul className="flex flex-col gap-0.5 text-11 text-st-down" data-testid={testId}>
      {problems.map((problem, index) => (
        <li key={index}>{problem}</li>
      ))}
    </ul>
  )
}

export function EditorError({ error }: { error?: string }) {
  if (!error) return null
  return (
    <p role="alert" className="text-xs text-st-down">
      {error}
    </p>
  )
}
